import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './AuthContext';

import Header from './components/Header';
import Footer from './components/Footer';
import Auth from './components/Auth';
import CreditPurchase from './components/CreditPurchase';
import ProtectedRoute from './components/ProtectedRoute';
import Cart from './components/Cart';
// import Horoscope from './components/Horoscope_temp';
// import Kundli from './components/Kundli';
// import CallGuru from './components/CallGuru_temp';
// import Submuhrat from './components/Submuhrat_temp';
// import MoonTracker from './components/MoonTracker';
// import BookPooja from './components/BookPooja';
// import Refer from './components/refer';
import Profile from './components/ProfileModal';
// import Feedback from './components/Feedback';
import Terms from './components/terms';
import AboutUs from './components/AboutUs';
import ContactUs from './components/ContactUs';
// import KundaliWidget from './components/kundlitesting';
// import Test from './components/test';
// import ReportsPage from './components/Reports/ReportsPage';
import Policy from './components/Policy';
import PaymentResult from './components/PaymentResult';

import './App.css';

import { StatusBar } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

import { App as CapacitorApp } from '@capacitor/app';
import PaymentHistory from './components/PaymentHistory';
import Products from './components/Products';
import ProductDetails from './components/ProductDetails';
import Checkout from './components/Checkout';
import OrderConfirmation from './components/OrderConfirmation';
import OrderHistory from './components/OrderHistory';
import ShipmentTracking from './components/ShipmentTracking';

// ✅ Simplified: Only app lifecycle and update listeners (no deep links)
const GlobalListeners = () => {
  useEffect(() => {
    console.log('🚀 [GLOBAL] Setting up app-wide listeners...');
    const listeners = [];

    const setupListeners = async () => {
      // ========================================
      // 📡 1. APP LIFECYCLE LISTENERS
      // ========================================
      
      const pauseListener = await CapacitorApp.addListener('pause', () => {
        console.log('⏸️ [GLOBAL] App moved to background');
      });
      listeners.push(pauseListener);

      const resumeListener = await CapacitorApp.addListener('resume', () => {
        console.log('▶️ [GLOBAL] App returned to foreground');
      });
      listeners.push(resumeListener);

      const stateListener = await CapacitorApp.addListener('appStateChange', (state) => {
        console.log('🔄 [GLOBAL] App state isActive:', state.isActive);
      });
      listeners.push(stateListener);

      // ========================================
      // 📡 2. CAPGO UPDATE LISTENERS
      // ========================================

      const downloadFailListener = await CapacitorUpdater.addListener('downloadFailed', (info) => {
        console.log('❌ [GLOBAL UPDATE] Download failed:', info);
      });
      listeners.push(downloadFailListener);

      const noUpdateListener = await CapacitorUpdater.addListener('noNeedUpdate', (info) => {
        console.log('✅ [GLOBAL UPDATE] No update needed:', info);
      });
      listeners.push(noUpdateListener);

      const readyListener = await CapacitorUpdater.addListener('appReady', (info) => {
        console.log('🚀 [GLOBAL UPDATE] App ready:', info);
      });
      listeners.push(readyListener);

      console.log('✅ [GLOBAL] All app-wide listeners registered:', listeners.length);
    };

    setupListeners();

    return () => {
      console.log('🧹 [GLOBAL] Cleaning up all app-wide listeners');
      listeners.forEach((listener, index) => {
        try {
          listener.remove();
          console.log(`✅ [GLOBAL] Listener ${index + 1} removed`);
        } catch (e) {
          console.log(`⚠️ [GLOBAL] Could not remove listener ${index + 1}`);
        }
      });
    };
  }, []);

  return null;
};

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
// const [isLightTheme, setIsLightTheme] = (false);

  const isAuthPage = location.pathname === '/';
  const isHomePage = location.pathname === '/home';
  const isTermsPage = location.pathname === '/terms';
  const isAboutPage = location.pathname === '/aboutus';
  const isContactPage = location.pathname === '/contactus';
  const istest = location.pathname === '/test';
  const isReportsPage = location.pathname === '/reports';
  const isPolicy = location.pathname === '/policy';
  const isProductsPage = location.pathname === '/products';
  const isCartPage = location.pathname === '/cart';
  const isPaymentResultPage = 
    location.pathname === '/payment/success' || 
    location.pathname === '/payment/failure' || 
    location.pathname === '/paymentResult' ||
    location.pathname.startsWith('/paymentResult');
  
  const isPublicPage = isAuthPage || isTermsPage || isAboutPage || isContactPage || 
                       istest || isReportsPage || isPolicy || isPaymentResultPage || 
                       isProductsPage || isCartPage || isHomePage;
//  useEffect(() => {
//     if (isLightTheme) {
//       document.body.classList.add('light-theme');
//     } else {
//       document.body.classList.remove('light-theme');
//     }
//   }, [isLightTheme]);
// ✅ Detect native app

  useEffect(() => {
    const detectNativeApp = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera;

      if (window.ReactNativeWebView) return true;
      if (window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches) return true;

      const webViewPatterns = [
        /wv/i, /WebView/i, /Android.*Version\/.*Chrome/i,
        /; wv\)/i, /iPhone.*AppleWebKit(?!.*Safari)/i
      ];

      if (webViewPatterns.some(pattern => pattern.test(ua))) return true;

      const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
      const isStandardBrowser = /Chrome|CriOS|Safari/i.test(ua) && !/wv/i.test(ua);

      return isMobile && !isStandardBrowser;
    };

    const isNative = detectNativeApp();

    if (isNative) {
      document.documentElement.classList.add('native-app-mode');
      document.body.classList.add('native-app-mode');
    }
  }, []);

  // ✅ FIXED: Route validation with payment page priority
  useEffect(() => {
    const currentPath = location.pathname;
    const validPaths = new Set([
      '/home', '/chat', '/credits', '/kundli', '/horoscope',
      '/submuhrat', '/call', '/moon', '/pooja', '/refer',
      '/profile', '/feedback', '/aboutus', '/contactus', '/kundlitesting', 
      '/test', '/terms', '/reports', '/policy', '/paymentResult', 
      '/payment/success', '/payment/failure', '/payment/history', '/cart', '/checkout', '/products', '/orders'
    ]);
    
    console.log('🔍 Route validation - path:', currentPath);
    
    if (loading) return;

    // ✅ PRIORITY 0: Always allow payment pages FIRST
    if (currentPath.startsWith('/paymentResult') || currentPath.startsWith('/payment/')) {
      console.log('💳 Payment page - allowing access');
      return;
    }

    // ✅ PRIORITY 0.5: Allow order confirmation page (dynamic route)
    if (currentPath.startsWith('/order-confirmation/')) {
      console.log('📦 Order confirmation page - allowing access');
      return;
    }

    if (currentPath.startsWith('/orders/') && currentPath.endsWith('/track')) {
      console.log('🚚 Order tracking page - allowing access');
      return;
    }

     // ✅ PRIORITY 0.6: Allow product details page
    if (currentPath.startsWith('/product/')) {
      console.log('📦 Product details page - allowing access');
      return;
    }

    // PRIORITY 1: Allow public pages
    if (isPublicPage) {
      console.log('📄 Public page accessible');
      if (user && isAuthPage) {
        console.log('✅ Logged in on auth page, redirecting to /home');
        navigate('/home', { replace: true });
      }
      return;
    }
    
    // PRIORITY 2: Not logged in
    if (!user) {
      console.log('🚫 Not logged in, redirecting to /');
      navigate('/', { replace: true });
      return;
    }

    // PRIORITY 3: Invalid route
    if (!validPaths.has(currentPath)) {
      console.log('⚠️ Invalid route, redirecting to /home');
      navigate('/home', { replace: true });
    }

  }, [user, loading, location.pathname, navigate, isPublicPage, isAuthPage]);

// ✅ Prevent back on login
useEffect(() => {
  if (isAuthPage && !user?.full_name) {
    window.history.pushState(null, '', window.location.href);

    const handlePopState = (e) => {
      if (window.location.pathname === '/') {
        e.preventDefault();
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }
}, [isAuthPage, user]);

// ✅ Status bar (Capacitor)
useEffect(() => {
  if (Capacitor.isNativePlatform()) {
    StatusBar.setStyle({ style: 'DARK' });
    StatusBar.setBackgroundColor({ color: '#1a1a2e' });
    StatusBar.setOverlaysWebView({ overlay: false });
    document.documentElement.classList.add('native-app-mode');
  }
}, []);

if (loading) {
  return <div className="loading-screen"></div>;
}

return (
   <div className="app-layout">
    <Header />
    
      
      {/* Theme Toggle Button 
      <button
        onClick={() => setIsLightTheme(!isLightTheme)}
        style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          zIndex: 9999,
          padding: '10px 15px',
          background: isLightTheme ? '#222' : '#fff',
          color: isLightTheme ? '#fff' : '#222',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: 'bold',
          fontSize: '14px'
        }}
      >
        {isLightTheme ? '🌙 Dark Mode' : '☀️ Light Mode'}
      </button>
*/}
    <main className={`main-content ${!isPublicPage ? 'with-header-footer' : ''}`}>
      {children}
    </main>
    <Footer />
  </div>
);
};

function App() {
  return (
    <GoogleOAuthProvider clientId="1039681193782-obmnqsvo8dvct21i1fum6dov74i0iqn2.apps.googleusercontent.com">
      <AuthProvider>
        <Router>
        <GlobalListeners /> {/* ✅ Simplified: No deep links, no browser closing */}
          <Layout>
            <Routes>
              <Route path="/" element={<Auth />} />
              {/* <Route path="/" element={<Products />} /> */}
              {/* <Route path="/products" element={<Products />} /> */}
              <Route path="/product/:id" element={<ProductDetails />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute><OrderHistory /></ProtectedRoute>} />
              <Route path="/orders/:orderId/track" element={<ProtectedRoute><ShipmentTracking /></ProtectedRoute>} />
              <Route path="/order-confirmation/:orderId" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/aboutus" element={<AboutUs />} />
              <Route path="/contactus" element={<ContactUs />} />

              {/* <Route path="/test" element={<Test />} /> */}
              {/* <Route path="/reports" element={<ReportsPage />} /> */}
              <Route path="/policy" element={<Policy />} />
              <Route path="/home" element={<Products />} />
              <Route path="/paymentResult" element={<PaymentResult />} />
              <Route path="/payment/success" element={<PaymentResult />} />
              <Route path="/payment/failure" element={<PaymentResult />} />
              <Route path="/payment/history" element={<ProtectedRoute><PaymentHistory /></ProtectedRoute>} />
              {/* <Route path="/chat" element={<ProtectedRoute><ChatBot /></ProtectedRoute>} /> */}
              <Route path="/credits" element={<ProtectedRoute><CreditPurchase /></ProtectedRoute>} />
              {/* <Route path="/kundli" element={<ProtectedRoute><Kundli /></ProtectedRoute>} /> */}
              {/* <Route path="/horoscope" element={<ProtectedRoute><Horoscope /></ProtectedRoute>} /> */}
              {/* <Route path="/submuhrat" element={<ProtectedRoute><Submuhrat /></ProtectedRoute>} /> */}
              {/* <Route path="/call" element={<ProtectedRoute><CallGuru /></ProtectedRoute>} /> */}
              {/* <Route path="/moon" element={<ProtectedRoute><MoonTracker /></ProtectedRoute>} /> */}
              {/* <Route path="/pooja" element={<ProtectedRoute><BookPooja /></ProtectedRoute>} /> */}
              {/* <Route path="/refer" element={<ProtectedRoute><Refer /></ProtectedRoute>} /> */}
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              {/* <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} /> */}
              {/* <Route path="/kundlitesting" element={<ProtectedRoute><KundaliWidget /></ProtectedRoute>} /> */}
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
          </Layout>
        </Router>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
